@layer refine.base {
  :host {
    --_cell-size: var(--otp-cell-size, var(--size-12));
    --_cell-gap: var(--otp-cell-gap, var(--size-2));
    --_cell-font-size: var(--otp-cell-font-size, var(--text-xl));
    --_cell-radius: var(--otp-cell-radius, var(--rounded-lg));
    --_cell-border: var(--otp-cell-border, var(--border));

    /* --_bg/--_border-color (not --_cell-*-prefixed, unlike the rest of this component's tokens)
       — this is the shared name `fieldVariantMixin` reads/writes, matching `ore-input`/`ore-
       textarea`/`ore-message-composer`. Public override is still `--otp-cell-bg`/`--otp-cell-
       border-color` either way. */
    --_border-color: var(--otp-cell-border-color, var(--color-contrast-300));
    --_bg: var(--otp-cell-bg, var(--color-canvas));
    --_cell-focus-border: var(--otp-cell-focus-border, var(--_theme-base, var(--color-contrast-700)));

    display: inline-flex;
    flex-direction: column;
    gap: var(--size-1);
  }

  .otp-group {
    display: flex;
    gap: var(--_cell-gap);
    align-items: center;
  }

  .cell {
    box-sizing: border-box;
    display: block;
    width: var(--_cell-size);
    height: var(--_cell-size);
    padding: 0;
    font-size: var(--_cell-font-size);
    font-weight: var(--font-semibold);
    line-height: 1;
    color: var(--color-contrast-900);
    text-align: center;
    caret-color: var(--_cell-focus-border);
    background: var(--_bg);
    border: var(--_cell-border) solid var(--_border-color);
    border-radius: var(--_cell-radius);
    transition:
      border-color var(--transition-fast),
      box-shadow var(--transition-fast);
  }

  .cell::selection {
    background: var(--_theme-backdrop, var(--color-contrast-100));
  }

  /* Ring box-shadow lives in `@layer refine.variants` below, not here — every variant's own
     rest-state `box-shadow` (`fieldVariantMixin`'s solid/outline/ghost rules) is *also* in
     `refine.variants`, and cascade layers decide the winner before specificity ever gets
     consulted. A `refine.base` box-shadow here would always lose to those, focused or not. */
  .cell:focus {
    outline: none;
    border-color: var(--_cell-focus-border);
  }

  .cell:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .separator {
    font-size: var(--_cell-font-size);
    color: var(--color-contrast-400);
    user-select: none;
  }
}

/*
 * The solid/flat/bordered/outline/ghost variant switch (previously `@layer refine.variants`
 * here) now lives in `fieldVariantMixin({ accentVar: '--_cell-focus-border', container: '.cell',
 * tokenPrefix: 'otp-cell' })` in `otp-input.ts`'s `styles` array — shared with `ore-input`/
 * `ore-textarea`/`ore-message-composer` instead of hand-duplicated. `accentVar` overrides the
 * shared `--_theme-focus` accent with this component's own per-cell focus-color token, since
 * that (not the shared theme-focus token) is what `flat`/`bordered` mixed into their border
 * color here even before this shared version existed.
 */
@layer refine.variants {
  /* Focus glow — the same `--_theme-shadow` halo `ore-input`/`ore-textarea`/`ore-message-
     composer` use, kept out of the shared mixin only because `.cell` is the focusable element
     itself (not a wrapper, so this is `:focus` not `:focus-within`, and it targets `.cell`
     directly rather than a mixin-supplied `container`). `flat` excluded — the mixin's own
     `flat:focus-within` rule already sets a bespoke `box-shadow` for it. */
  :host(:not([disabled], [variant='flat'])) .cell:focus {
    box-shadow: var(--_theme-shadow, var(--color-primary-focus-shadow));
  }
}

@layer refine.utilities {
  :host([size='sm']) {
    --_cell-size: var(--size-9);
    --_cell-font-size: var(--text-base);
  }

  :host([size='lg']) {
    --_cell-size: var(--size-14);
    --_cell-font-size: var(--text-2xl);
  }
}
